import React, { useEffect } from 'react'
import { Box, IconButton, Portal } from '@mui/material'
import { XMarkIcon } from '@heroicons/react/24/outline'
import { INTERAX_COLORS } from '../../constants/colors'

/** z-index for modals so they appear above header (1000) and fixed buttons (1001) */
export const MODAL_Z_INDEX = 10001

interface DefaultModalProps {
  isOpen: boolean
  onClose: () => void
  children: React.ReactNode
  className?: string
  showCloseButton?: boolean
  maxWidth?: string
  backgroundColor?: string
  backdropColor?: string
  /** When true, clicking the backdrop does NOT close the modal (use the X / Escape instead). */
  disableBackdropClose?: boolean
}

/**
 * DefaultModal - Reusable modal component with blur background and click outside to close
 * Base component for all modals in the application
 */
export const DefaultModal: React.FC<DefaultModalProps> = ({
  isOpen,
  onClose,
  children,
  className = '',
  showCloseButton = true,
  maxWidth = '600px',
  backgroundColor = INTERAX_COLORS.heritageBlue,
  backdropColor = 'rgba(0, 0, 0, 0.7)',
  disableBackdropClose = false
}) => {
  // Handle click outside to close (unless disabled for this modal)
  const handleBackdropClick = (e: React.MouseEvent) => {
    if (disableBackdropClose) return
    if (e.target === e.currentTarget) {
      onClose()
    }
  }

  // Handle escape key to close
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        onClose()
      }
    }

    if (isOpen) {
      document.addEventListener('keydown', handleEscape)
      document.body.style.overflow = 'hidden' // Prevent background scrolling
    }

    return () => {
      document.removeEventListener('keydown', handleEscape)
      document.body.style.overflow = 'unset'
    }
  }, [isOpen, onClose])

  if (!isOpen) return null

  const modalContent = (
    <Box
      className={className}
      onClick={handleBackdropClick}
      sx={{
        position: 'fixed',
        top: 0,
        left: 0,
        width: '100%',
        height: '100%',
        backgroundColor: backdropColor,
        backdropFilter: 'blur(8px)', // Blur effect
        zIndex: MODAL_Z_INDEX,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        p: 2
      }}
    >
      {/* Modal Content */}
      <Box
        sx={{
          width: '100%',
          maxWidth: maxWidth,
          backgroundColor: backgroundColor,
          borderRadius: '16px',
          p: 4,
          position: 'relative',
          boxShadow: '0 8px 32px rgba(0, 0, 0, 0.3)'
        }}
      >
        {/* Close Button - Top Right */}
        {showCloseButton && (
          <IconButton
            onClick={onClose}
            sx={{
              position: 'absolute',
              top: 16,
              right: 16,
              color: INTERAX_COLORS.white,
              backgroundColor: 'rgba(255, 255, 255, 0.1)',
              zIndex: 20,
              '&:hover': {
                backgroundColor: 'rgba(255, 255, 255, 0.2)'
              }
            }}
          >
            <XMarkIcon className="w-5 h-5" />
          </IconButton>
        )}

        {/* Modal Content */}
        {children}
      </Box>
    </Box>
  )

  return <Portal>{modalContent}</Portal>
}
